import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { emailService } from '@/services/emailService';
import api from '@/services/api';
import type { EmailTemplate, DisparoEmail, CampanhaPreview, DisparoEmailContato, DeliveryStatus, EntregaResumo } from '@/types/email';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function campanhaStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'CONCLUIDO') return 'default';
  if (status === 'CANCELADO' || status === 'PAUSADO') return 'secondary';
  return 'outline';
}

const DELIVERY_LABEL: Record<DeliveryStatus, string> = {
  SENT: 'Enviado', DELIVERED: 'Entregue', OPENED: 'Aberto',
  BOUNCED: 'Bounce', COMPLAINED: 'Spam',
};
function deliveryVariant(s: string | null): 'outline' | 'destructive' | 'secondary' {
  if (s === 'BOUNCED' || s === 'COMPLAINED') return 'destructive';
  if (s === 'DELIVERED' || s === 'OPENED') return 'secondary';
  return 'outline';
}
/** Formata um timestamp ISO para data/hora curta pt-BR; '—' quando ausente/inválido. */
function fmtDateTime(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export default function EmailCampanhasTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const clientId = isAdmin ? (selectedClientId || undefined) : (user?.client_id ?? undefined);

  const [templateId, setTemplateId] = useState<number | ''>('');
  const [fromName, setFromName] = useState('');
  const [fromLocalPart, setFromLocalPart] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CampanhaPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const [contatosDe, setContatosDe] = useState<DisparoEmail | null>(null);

  const { data: clients = [], isLoading: loadingClients } = useQuery<{ id: number; legal_name?: string; trade_name?: string }[]>({
    queryKey: ['email-clients'],
    queryFn: async () => {
      const d = await api.getClients();
      return Array.isArray(d) ? d : [];
    },
    enabled: isAdmin,
  });

  const { data: templates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ['email-templates', clientId],
    queryFn: () => emailService.listTemplates(clientId),
    enabled: !!clientId,
  });

  const { data: remetente } = useQuery({
    queryKey: ['email-remetente', clientId],
    queryFn: () => emailService.getRemetente(clientId as number),
    enabled: !!clientId,
    refetchOnWindowFocus: false, // não sobrescrever edição em andamento do remetente num refetch de foco
  });

  // Pré-preenche o remetente a partir da config efetiva do cliente (editável).
  useEffect(() => {
    const ef = remetente?.efetivo;
    if (!ef) return;
    setFromName(ef.from_name ?? '');
    setFromLocalPart(ef.from_email ? ef.from_email.split('@')[0] : '');
    setReplyTo(ef.reply_to ?? '');
  }, [remetente]);

  const { data: campanhas = [], isLoading } = useQuery<DisparoEmail[]>({
    queryKey: ['email-campanhas', clientId],
    queryFn: () => emailService.listCampanhas(clientId),
    enabled: !!clientId,
    refetchInterval: (query) => {
      const data = query.state.data as DisparoEmail[] | undefined;
      return data?.some((c) => c.status === 'ENVIANDO') ? 4000 : false;
    },
  });

  const { data: contatosData } = useQuery({
    queryKey: ['email-campanha-contatos', contatosDe?.id],
    queryFn: () => emailService.getCampanhaContatos(contatosDe!.id, 1, 100),
    enabled: !!contatosDe,
  });

  const { data: entregaResumo } = useQuery({
    queryKey: ['email-campanha-entrega-resumo', contatosDe?.id],
    queryFn: () => emailService.getCampanhaEntregaResumo(contatosDe!.id),
    enabled: !!contatosDe,
  });

  function resetForm() {
    setTemplateId('');
    setFromName('');
    setFromLocalPart('');
    setReplyTo('');
    setFile(null);
    setPreview(null);
    setMapping({});
  }

  const previewMut = useMutation({
    mutationFn: async () => {
      if (!file || !templateId || !clientId) throw new Error('Selecione template e arquivo');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('client_id', String(clientId));
      fd.append('email_template_id', String(templateId));
      return emailService.previewCampanha(fd);
    },
    onSuccess: (p) => {
      setPreview(p);
      setMapping(p.suggestedMapping || {});
    },
    onError: (e: any) => toast({ title: 'Erro', description: e?.message || 'Falha ao pré-visualizar.', variant: 'destructive' }),
  });

  const criarMut = useMutation({
    mutationFn: async () => {
      if (!file || !templateId || !clientId) throw new Error('Selecione template e arquivo');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('client_id', String(clientId));
      fd.append('email_template_id', String(templateId));
      if (fromName) fd.append('from_name', fromName);
      if (fromLocalPart) fd.append('from_local_part', fromLocalPart);
      if (replyTo) fd.append('reply_to', replyTo);
      fd.append('column_mapping', JSON.stringify(mapping));
      return emailService.criarCampanha(fd);
    },
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['email-campanhas'] });
      toast({ title: 'Campanha criada', description: `${r.created} contatos (${r.invalidos} inválidos).` });
      resetForm();
    },
    onError: (e: any) => toast({ title: 'Erro', description: e?.message || 'Falha ao criar campanha.', variant: 'destructive' }),
  });

  const iniciarMut = useMutation({
    mutationFn: (id: number) => emailService.iniciarCampanha(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-campanhas'] });
      toast({ title: 'Campanha iniciada', description: 'O envio começou.' });
    },
    onError: (e: any) => toast({ title: 'Erro', description: e?.message || 'Falha ao iniciar.', variant: 'destructive' }),
  });

  const cancelarMut = useMutation({
    mutationFn: (id: number) => emailService.cancelarCampanha(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-campanhas'] });
      toast({ title: 'Campanha cancelada' });
    },
    onError: (e: any) => toast({ title: 'Erro', description: e?.message || 'Falha ao cancelar.', variant: 'destructive' }),
  });

  const pausarMut = useMutation({
    mutationFn: (id: number) => emailService.pausarCampanha(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-campanhas'] });
      toast({ title: 'Campanha pausada' });
    },
    onError: (e: any) => toast({ title: 'Erro', description: e?.message || 'Falha ao pausar.', variant: 'destructive' }),
  });

  const retomarMut = useMutation({
    mutationFn: (id: number) => emailService.retomarCampanha(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-campanhas'] });
      toast({ title: 'Campanha retomada', description: 'O envio voltou a rodar.' });
    },
    onError: (e: any) => toast({ title: 'Erro', description: e?.message || 'Falha ao retomar.', variant: 'destructive' }),
  });

  const reenviarFalhasMut = useMutation({
    mutationFn: (id: number) => emailService.reenviarFalhasCampanha(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-campanhas'] });
      toast({ title: 'Reenviando falhas', description: 'Os contatos com falha voltaram para a fila.' });
    },
    onError: (e: any) => toast({ title: 'Erro', description: e?.message || 'Falha ao reenviar.', variant: 'destructive' }),
  });

  const podeMontar = !!clientId;
  const placeholders = preview?.placeholders ?? [];
  const mappingComplete = placeholders.every((p) => !!mapping[p]);

  return (
    <div className="space-y-6">
      {isAdmin && (
        <Card>
          <CardHeader><CardTitle>Cliente</CardTitle></CardHeader>
          <CardContent>
            <Select
              value={selectedClientId ? String(selectedClientId) : ''}
              onValueChange={(v) => { setSelectedClientId(v ? Number(v) : ''); resetForm(); }}
              disabled={loadingClients}
            >
              <SelectTrigger><SelectValue placeholder={loadingClients ? 'Carregando...' : 'Selecione o cliente'} /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.trade_name || c.legal_name || `Cliente ${c.id}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {!podeMontar ? (
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            {isAdmin ? 'Selecione um cliente para montar campanhas.' : 'Seu usuário não está associado a um cliente.'}
          </p>
        </CardContent></Card>
      ) : (
        <>
          <Card>
            <CardHeader><CardTitle>Nova campanha</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Template</Label>
                <Select value={templateId ? String(templateId) : ''} onValueChange={(v) => { setTemplateId(v ? Number(v) : ''); setPreview(null); setMapping({}); }}>
                  <SelectTrigger><SelectValue placeholder={templates.length ? 'Selecione um template' : 'Nenhum template cadastrado'} /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (<SelectItem key={t.id} value={String(t.id)}>{t.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="c-fromname">Nome do remetente</Label>
                  <Input id="c-fromname" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Cobrança Empresa X" />
                </div>
                <div>
                  <Label htmlFor="c-localpart">Remetente (@{remetente?.efetivo?.dominio ?? 'mail.atomos.tech'})</Label>
                  <Input id="c-localpart" value={fromLocalPart} onChange={(e) => setFromLocalPart(e.target.value)} placeholder="empresax" />
                </div>
                <div>
                  <Label htmlFor="c-replyto">Reply-To</Label>
                  <Input id="c-replyto" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="financeiro@empresax.com.br" />
                </div>
              </div>

              <div>
                <Label htmlFor="c-file">Planilha de contatos (1ª coluna = email)</Label>
                <Input id="c-file" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); }} />
              </div>

              <Button
                variant="outline"
                onClick={() => previewMut.mutate()}
                disabled={!file || !templateId || previewMut.isPending}
              >
                {previewMut.isPending ? 'Lendo...' : 'Pré-visualizar e mapear'}
              </Button>

              {preview && (
                <div className="space-y-4 border-t pt-4">
                  <p className="text-sm text-muted-foreground">{preview.totalRows} linha(s). Mapeie cada variável a uma coluna:</p>
                  {placeholders.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Este template não tem variáveis.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {placeholders.map((p) => (
                        <div key={p}>
                          <Label>{`{{${p}}}`}</Label>
                          <Select value={mapping[p] || ''} onValueChange={(v) => setMapping((m) => ({ ...m, [p]: v }))}>
                            <SelectTrigger><SelectValue placeholder="Selecione a coluna" /></SelectTrigger>
                            <SelectContent>
                              {preview.headers.map((h) => (<SelectItem key={h} value={h}>{h}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button onClick={() => criarMut.mutate()} disabled={criarMut.isPending || !mappingComplete}>
                    {criarMut.isPending ? 'Criando...' : 'Criar campanha'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Campanhas</CardTitle></CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Carregando...</p>
              ) : campanhas.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma campanha cadastrada.</p>
              ) : (
                <div className="space-y-2">
                  {campanhas.map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-4 border rounded-lg p-3">
                      <div>
                        <p className="font-medium">{c.nome}</p>
                        <p className="text-sm text-muted-foreground">
                          {c.total_contatos} contato(s) · {c.enviados} enviado(s) · {c.falhas} falha(s) · {new Date(c.created_at).toLocaleString('pt-BR')}
                        </p>
                        {c.origem_lote_id && (
                          <span className="text-xs text-gray-500">Gerado do lote #{c.origem_lote_id}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={campanhaStatusVariant(c.status)}>{c.status}</Badge>
                        {c.status === 'RASCUNHO' && (
                          <Button size="sm" onClick={() => iniciarMut.mutate(c.id)} disabled={iniciarMut.isPending && iniciarMut.variables === c.id}>Iniciar</Button>
                        )}
                        {c.status === 'ENVIANDO' && (
                          <>
                            <Button size="sm" variant="secondary" onClick={() => pausarMut.mutate(c.id)} disabled={pausarMut.isPending && pausarMut.variables === c.id}>Pausar</Button>
                            <Button size="sm" variant="destructive" onClick={() => cancelarMut.mutate(c.id)} disabled={cancelarMut.isPending && cancelarMut.variables === c.id}>Cancelar</Button>
                          </>
                        )}
                        {c.status === 'PAUSADO' && (
                          <>
                            <Button size="sm" onClick={() => retomarMut.mutate(c.id)} disabled={retomarMut.isPending && retomarMut.variables === c.id}>Retomar</Button>
                            <Button size="sm" variant="destructive" onClick={() => cancelarMut.mutate(c.id)} disabled={cancelarMut.isPending && cancelarMut.variables === c.id}>Cancelar</Button>
                          </>
                        )}
                        {c.status === 'CANCELADO' && (
                          <Button size="sm" onClick={() => retomarMut.mutate(c.id)} disabled={retomarMut.isPending && retomarMut.variables === c.id}>Retomar</Button>
                        )}
                        {(c.status === 'CONCLUIDO' || c.status === 'CANCELADO' || c.status === 'PAUSADO') && c.falhas > 0 && (
                          <Button size="sm" variant="outline" onClick={() => reenviarFalhasMut.mutate(c.id)} disabled={reenviarFalhasMut.isPending && reenviarFalhasMut.variables === c.id}>Reenviar falhas</Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setContatosDe(c)}>Ver contatos</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!contatosDe} onOpenChange={(o) => { if (!o) setContatosDe(null); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Contatos — {contatosDe?.nome}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Exibindo {contatosData?.contatos.length ?? 0} de {contatosData?.total ?? 0} contato(s)
          </p>
          {entregaResumo && (
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-2">
              <span>Total: <b>{entregaResumo.total}</b></span>
              <span>Entregues: <b>{entregaResumo.entregues}</b></span>
              <span>Abertos: <b>{entregaResumo.abertos}</b></span>
              <span>Bounces: <b>{entregaResumo.bounces}</b></span>
              <span>Spam: <b>{entregaResumo.complaints}</b></span>
            </div>
          )}
          <div className="max-h-[60vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Enviado em</TableHead>
                  <TableHead>Entrega</TableHead>
                  <TableHead>Aberto em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(contatosData?.contatos ?? []).map((ct) => (
                  <TableRow key={ct.id}>
                    <TableCell className="font-mono text-xs">{ct.email_destino}</TableCell>
                    <TableCell>
                      <Badge variant={ct.status_envio === 'EMAIL_INVALIDO' || ct.status_envio === 'FALHA' ? 'destructive' : 'outline'}>{ct.status_envio}</Badge>
                      {ct.motivo_falha && <span className="ml-2 text-xs text-muted-foreground">{ct.motivo_falha}</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(ct.enviado_at)}</TableCell>
                    <TableCell>
                      {ct.delivery_status ? (
                        <Badge variant={deliveryVariant(ct.delivery_status)}>
                          {DELIVERY_LABEL[ct.delivery_status] ?? ct.delivery_status}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                      {ct.falha_entrega_motivo && (
                        <span className="ml-2 text-xs text-muted-foreground">{ct.falha_entrega_motivo}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(ct.aberto_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
